import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchCustomers, createCustomer, updateCustomer } from '../services/customerService';
import { updateSubscriptionFields } from '../services/subscriptionService';
import { useOrganization } from '../contexts/OrganizationContext';
import { getSubscriptionByGuid } from '../services/subscriptionService';
import { v4 as uuidv4 } from 'uuid';
import CONFIG from '../config/config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {
    Box,
    Button,
    TextField,
    Typography,
    LinearProgress,
    FormControlLabel,
    Checkbox,
    MenuItem,
    Paper,
} from '@mui/material';

const communicationMethods = ['Email', 'Phone', 'Messaging'];
const languages = ['English', 'Spanish', 'French', 'German', 'Russian', 'Other'];

const CustomerDetails = () => {
    const navigate = useNavigate();
    const { customerGuid } = useParams();
    const { selectedOrg } = useOrganization();
    const [subscription, setSubscription] = useState(null);

    const [customerData, setCustomerData] = useState({
        customerGuid: null,
        businessName: '',
        contactName: '',
        email: '',
        phoneNumber: '',
        secondaryContactName: '',
        secondaryPhone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        isMailingSameAsBusiness: true,
        mailingAddress: '',
        mailingCity: '',
        mailingState: '',
        mailingZipCode: '',
        mailingCountry: '',
        notes: '',
        isNewsletterSubscribed: true,
        preferredContactMethod: '',
        communicationLanguage: '',
        createdAt: null,
        updatedAt: null,
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEdit, setIsEdit] = useState(!!customerGuid);
    const [isDirty, setIsDirty] = useState(false);
    const [modifiedFields, setModifiedFields] = useState({});
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        const loadSubscriptionData = async () => {
            if (selectedOrg) {
                try {
                    const subscriptionData = await getSubscriptionByGuid(selectedOrg);
                    if (subscriptionData) {
                        setSubscription(subscriptionData.subscription);
                    } else {
                        throw new Error('Subscription not found');
                    }
                } catch (error) {
                    console.error('Error loading subscription data:', error);
                    setError('Failed to load subscription data.');
                }
            } else {
                setSubscription(null); // Clear subscription if no subscriptionGuid exists
            }
        };

        loadSubscriptionData();
    }, [selectedOrg]);

    useEffect(() => {
        const loadCustomerDetails = async () => {
            if (isEdit) {
                try {
                    const customers = await fetchCustomers(selectedOrg);
                    const customer = customers.find((c) => c.customerGuid === customerGuid);
                    if (customer) {
                        setCustomerData({ ...customer });
                    } else {
                        setError('Customer not found.');
                    }
                } catch (err) {
                    console.error('Error fetching customer details:', err);
                    setError('Failed to load customer details.');
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false); // No need to fetch data when creating a new customer
            }
        };

        loadCustomerDetails();
    }, [isEdit, customerGuid, selectedOrg]);

    const validateFields = () => {
        if (!selectedOrg) {
            console.error('No organization selected.');
            return;
        }

        const errors = {};
        if (!customerData.businessName.trim()) errors.businessName = 'Business Name is required.';
        if (!customerData.contactName.trim()) errors.contactName = 'Contact Name is required.';
        if (!customerData.email.trim()) {
            errors.email = 'Email is required.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email)) {
            errors.email = 'Invalid email format.';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setIsDirty(true);
        setCustomerData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setModifiedFields((prevFields) => ({
            ...prevFields,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;

        setIsDirty(true);

        if (name === 'isMailingSameAsBusiness') {
            setCustomerData((prevData) => ({
                ...prevData,
                [name]: checked,
                mailingAddress: checked ? prevData.address : '',
                mailingCity: checked ? prevData.city : '',
                mailingState: checked ? prevData.state : '',
                mailingZipCode: checked ? prevData.zipCode : '',
                mailingCountry: checked ? prevData.country : '',
            }));
        } else {
            setCustomerData((prevData) => ({
                ...prevData,
                [name]: checked,
            }));
        }

        setModifiedFields((prevFields) => ({
            ...prevFields,
            [name]: checked,
        }));
    };

    const handleSave = async () => {
        if (!validateFields()) {
            return; // Prevent save if validation fails
        }

        try {
            if (isEdit) {
                // Update an existing customer
                const updatedData = {
                    ...modifiedFields,
                    version: (customerData.version || 1) + 1, // Increment version
                };

                await updateCustomer(customerGuid, updatedData);
            } else {
                // Create a new customer
                const newGuid = uuidv4();
                const now = new Date().toISOString(); // Get the current date/time in ISO8601 format
                const newCustomerPayload = {
                    customer: {
                        ...customerData,
                        customerGuid: newGuid,
                        createdAt: now,
                        updatedAt: now,
                    },
                };

                const createdCustomer = await createCustomer(newCustomerPayload);

                if (createdCustomer) {
                    if (!subscription?.assignedCustomers?.includes(newGuid)) {
                        await updateSubscriptionFields(
                            selectedOrg,
                            { assignedCustomers: [newGuid] },
                            'add'
                        );
                    }
                } else {
                    throw new Error('Failed to create customer. Subscription update aborted.');
                }
            }

            setIsDirty(false);
            setModifiedFields({});
            navigate('/customers');
        } catch (error) {
            console.error('Error saving/updating customer:', error);
            alert('An error occurred while saving the customer.');
        }
    };

    const handleCancel = () => {
        setIsDirty(false);
        setModifiedFields({});
        navigate('/customers');
    };

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', padding: 4 }}>
                <Typography>Loading Customer...</Typography>
                <LinearProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', padding: 4 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ paddingLeft: 4, paddingRight: 4, paddingBottom: 4, marginBottom: '80px', }}>
            <Button
                variant="text"
                onClick={handleCancel}
                startIcon={<ArrowBackIcon />}
                sx={{ marginBottom: 2 }}
            >
                Customer List
            </Button>
            <Typography variant="h5" gutterBottom>
                {isEdit ? 'Edit Customer Details' : 'Add Customer'}
            </Typography>

            {/* General Business Information */}
            <Paper
                elevation={1}
                sx={{
                    padding: 3,
                    borderRadius: 2,
                    marginBottom: 3,
                    border: '1px solid #e0e0e0',
                }}
            >
                <Typography variant="h6" gutterBottom>
                    General Business Information
                </Typography>
                <TextField
                    label="Business Name"
                    name="businessName"
                    value={customerData.businessName}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                    error={!!validationErrors.businessName}
                    helperText={validationErrors.businessName}
                />
            </Paper>

            {/* Contact Details */}
            <Paper
                elevation={1}
                sx={{
                    padding: 3,
                    borderRadius: 2,
                    marginBottom: 3,
                    border: '1px solid #e0e0e0',
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Contact Details
                </Typography>
                <TextField
                    label="Name"
                    name="contactName"
                    value={customerData.contactName}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                    error={!!validationErrors.contactName}
                    helperText={validationErrors.contactName}
                />
                <TextField
                    label="Email"
                    name="email"
                    value={customerData.email}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                    error={!!validationErrors.email}
                    helperText={validationErrors.email}
                />
                <TextField
                    label="Phone"
                    name="phoneNumber"
                    value={customerData.phoneNumber}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                />
                <TextField
                    label="Secondary Contact Name"
                    name="secondaryContactName"
                    value={customerData.secondaryContactName}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                />
                <TextField
                    label="Secondary Phone"
                    name="secondaryPhone"
                    value={customerData.secondaryPhone}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                />
            </Paper>

            {/* Address Information */}
            <Paper
                elevation={1}
                sx={{
                    padding: 3,
                    borderRadius: 2,
                    marginBottom: 3,
                    border: '1px solid #e0e0e0',
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Address Information
                </Typography>
                <TextField
                    label="Business Address"
                    name="address"
                    value={customerData.address}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                />
                <TextField
                    label="City"
                    name="city"
                    value={customerData.city}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                />
                <TextField
                    label="State"
                    name="state"
                    value={customerData.state}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                />
                <TextField
                    label="ZIP Code"
                    name="zipCode"
                    value={customerData.zipCode}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                />
                <TextField
                    label="Country"
                    name="country"
                    value={customerData.country}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={customerData.isMailingSameAsBusiness}
                            onChange={handleCheckboxChange}
                            name="isMailingSameAsBusiness"
                        />
                    }
                    label="Mailing Address Same as Business Address"
                />

                {!customerData.isMailingSameAsBusiness && (
                    <>
                        <TextField
                            label="Mailing Address"
                            name="mailingAddress"
                            value={customerData.mailingAddress}
                            onChange={handleChange}
                            fullWidth
                            autoComplete='off'
                            margin="normal"
                        />
                        <TextField
                            label="Mailing City"
                            name="mailingCity"
                            value={customerData.mailingCity}
                            onChange={handleChange}
                            fullWidth
                            autoComplete='off'
                            margin="normal"
                        />
                        <TextField
                            label="Mailing State"
                            name="mailingState"
                            value={customerData.mailingState}
                            onChange={handleChange}
                            fullWidth
                            autoComplete='off'
                            margin="normal"
                        />
                        <TextField
                            label="Mailing ZIP Code"
                            name="mailingZipCode"
                            value={customerData.mailingZipCode}
                            onChange={handleChange}
                            fullWidth
                            autoComplete='off'
                            margin="normal"
                        />
                        <TextField
                            label="Mailing Country"
                            name="mailingCountry"
                            value={customerData.mailingCountry}
                            onChange={handleChange}
                            fullWidth
                            autoComplete='off'
                            margin="normal"
                        />
                    </>
                )}
            </Paper>

            {/* Marketing Preferences */}
            <Paper
                elevation={1}
                sx={{
                    padding: 3,
                    borderRadius: 2,
                    marginBottom: 3,
                    border: '1px solid #e0e0e0',
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Marketing and Communication Preferences
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={customerData.isNewsletterSubscribed}
                            onChange={handleCheckboxChange}
                            name="isNewsletterSubscribed"
                        />
                    }
                    label="Subscribe to Newsletter"
                />
                <TextField
                    select
                    label="Preferred Contact Method"
                    name="preferredContactMethod"
                    value={customerData.preferredContactMethod}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                >
                    {communicationMethods.map((method) => (
                        <MenuItem key={method} value={method}>
                            {method}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Communication Language"
                    name="communicationLanguage"
                    value={customerData.communicationLanguage}
                    onChange={handleChange}
                    fullWidth
                    autoComplete='off'
                    margin="normal"
                >
                    {languages.map((lang) => (
                        <MenuItem key={lang} value={lang}>
                            {lang}
                        </MenuItem>
                    ))}
                </TextField>
            </Paper>

            {/* Pinned bar */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: `${CONFIG.MEASURE.SIDEBAR_WIDTH}px`,
                    right: 0,
                    display: 'flex',
                    //justifyContent: 'flex-end',
                    justifyContent: 'space-between', // Space between the text and buttons
                    alignItems: 'center',
                    //gap: 4,
                    padding: '8px',
                    backgroundColor: isDirty ? '#ffec9e' : '#f9f9f9',
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                    borderTop: '1px solid #dcdcdc',
                    //height: '48px',
                    zIndex: 999,
                }}
            >
                {/* Dirty state message or placeholder */}
                <Typography
                    variant="body2"
                    sx={{
                        color: isDirty ? '#6c757d' : 'transparent', // Hide text when not dirty
                        fontWeight: 400,
                        marginLeft: '16px', // Maintain alignment
                    }}
                >
                    Document modified.
                </Typography>

                {/* Buttons */}
                <Box sx={{ display: 'flex', gap: 1, marginRight: '20px' }}>
                    <Button variant="outlined" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={!isDirty} // Disable save button if no changes.
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default CustomerDetails;